import React from 'react';
import { useSetAtom, useAtom, useAtomValue } from 'jotai';

import {
  UpliftedAddressLookup,
  AddressManualEntryDialog,
  AddressManualEntryError,
  AddressManualEntryValues,
  DedicatedNmiSearchModal,
  constructDisplayLabel,
  getAddressFromOriginAddressId,
  getOriginAddresses,
  qasLookup,
  getOriginAddressesFromBatchId,
  QasProfile,
  type Address,
  type AddressResult,
  type ConnectionAddressObject,
  type IOriginAddressMeter,
  type AddressLookupProps,
} from '@origin-digital/address-lookup';

import {
  useNMIResolution,
  NMIResolution,
  NMIResolutionState,
} from '@origin-digital/nmi-resolution';

import { fromAddressToConnectionAddress } from './energy-address-mapper';
import { useEnergyAddressConfig } from './EnergyAddressLookupProvider';

import {
  originAddressesAtom,
  addressEntryDedicatedNmiSearchDialogOpenAtom,
  addressEntryManualDialogTabAtom,
  addressConfirmDialogOpenAtom,
  selectedAddressAtom,
  selectedEnergyAtom,
  unconfirmedAddressAtom,
  addressLookupStatusAtom,
  addressManualDialogWithTabAtom,
} from './atoms';

import { asyncResult } from './async-result';
import { useEnergyAddressLookup } from './useEnergyAddressLookup';
import { ORIGIN_ADDRESS_REVERSE_LOOKUP_API_URL, ORIGIN_ADDRESSES_API_URL } from './lookup-config';
import { createTraceHeaders } from './create-trace-headers';

export type UpliftedEnergyAddressLookupProps = {
  onAddressSelected: (address: ConnectionAddressObject) => void;
  onAddressCleared?: () => void;
  onAddressUnconfirmed?: (address: ConnectionAddressObject) => void;
  onAddressesUpdated?: (addresses: ConnectionAddressObject[]) => void;
  /** Overrides the CTA text in both address states; omitted preserves the existing labels. */
  ctaLabel?: string;
  /** Ignores CTA clicks while the consumer completes its own checks. */
  ctaBlocked?: boolean;
  ctaLoading?: boolean;
  onCtaClick?: () => void;
  originAddressId?: string;
  ariaLabel?: string;
  placeholder?: string;
  searchByNmi?: boolean;
  searchByMirn?: boolean;
  showEmptyAddressError?: boolean;
};

export const UpliftedEnergyAddressLookup: React.FC<UpliftedEnergyAddressLookupProps> = ({
  onAddressSelected,
  onAddressCleared,
  onAddressUnconfirmed,
  onAddressesUpdated,
  onCtaClick,
  ctaLabel,
  ctaBlocked: consumerCtaBlocked = false,
  ctaLoading = false,
  originAddressId,
  ariaLabel,
  placeholder,
  searchByNmi = true,
  searchByMirn = true,
  showEmptyAddressError = false,
}) => {
  const [addressValue, setAddressValue] = React.useState<string | undefined>();
  const [addressValueChanged, setAddressValueChanged] = React.useState(false);
  const [showEmptySearchError, setShowEmptySearchError] = React.useState(false);
  const prevAddressValue = React.useRef<string | undefined>(undefined);

  const {
    qasLookupAuthToken,
    originAddressesApiUrl,
    originAddressReverseLookupApiUrl,
    originBusinessTraceId,
  } = useEnergyAddressConfig();

  const reverseLookupUrl =
    originAddressReverseLookupApiUrl || ORIGIN_ADDRESS_REVERSE_LOOKUP_API_URL;

  const addressesUrl = originAddressesApiUrl || ORIGIN_ADDRESSES_API_URL;

  const { fetchAddresses, fetchAddressDetails } = qasLookup(qasLookupAuthToken, QasProfile.AUG);

  const { isNMIHardStop, isNMIServiceable, isNMIBelowThreshold } = useNMIResolution();
  const { nmiResolutionState, addresses } = useEnergyAddressLookup();

  const [endIcon, setEndIcon] = useAtom(addressLookupStatusAtom);

  const selectedEnergy = useAtomValue(selectedEnergyAtom);

  const [selectedAddress, setSelectedAddress] = useAtom(selectedAddressAtom);
  const [unconfirmedAddress, setUnconfirmedAddress] = useAtom(unconfirmedAddressAtom);
  const setOriginAddresses = useSetAtom(originAddressesAtom);

  const [manualAddressEntryDialogOpen, setManualAddressEntryDialogOpen] = useAtom(
    addressManualDialogWithTabAtom
  );

  const manualAddressTab = useAtomValue(addressEntryManualDialogTabAtom);

  const [dedicatedNmiSearchModalOpen, setDedicatedNmiSearchModalOpen] = useAtom(
    addressEntryDedicatedNmiSearchDialogOpenAtom
  );

  const [nmiConfirmationDialogOpen, setNmiConfirmationDialogOpen] = useAtom(
    addressConfirmDialogOpenAtom
  );

  React.useEffect(() => {
    setEndIcon('');
    (async function fetchInitialAddress() {
      if (originAddressId) {
        const response = await asyncResult(reverseAddressLookup(originAddressId));

        if (response.error) {
          setEndIcon('error');
        }

        return response.data;
      }
    })();
  }, []);

  React.useEffect(() => {
    prevAddressValue.current !== addressValue
      ? setAddressValueChanged(true)
      : setAddressValueChanged(false);

    prevAddressValue.current = addressValue;
  }, [addressValue, selectedEnergy]);

  const getAddress = async (
    originAddressId: string
  ): Promise<ConnectionAddressObject | undefined> => {
    const response = await asyncResult(
      getAddressFromOriginAddressId(
        reverseLookupUrl,
        originAddressId,
        createTraceHeaders(originBusinessTraceId)
      )
    );

    if (response.error) {
      setEndIcon('error');
      return undefined;
    }

    const addressDetails = fromAddressToConnectionAddress(response.data);

    return {
      ...addressDetails,
      originAddressId: addressDetails.originAddressId ?? (originAddressId || undefined),
    };
  };

  const getAddresses = async (
    address: AddressManualEntryValues | Address,
    isManualSearch: boolean
  ): Promise<ConnectionAddressObject[]> => {
    const searchConfigParams = {
      resolveAddress: true,
      includeType2Discovery: true,
      isManualAddress: isManualSearch,
    };

    const response = await asyncResult(
      getOriginAddresses(
        addressesUrl,
        undefined,
        {
          ...(address as Address),
          ...searchConfigParams,
        },
        createTraceHeaders(originBusinessTraceId)
      )
    );

    if (response.error) {
      setEndIcon('error');
      return [];
    }

    const addresses = response.data.data as unknown as Address[];
    return addresses.map(fromAddressToConnectionAddress);
  };

  const getAddressesFromBatchId = async (batchId: string): Promise<ConnectionAddressObject[]> => {
    const response = await asyncResult(
      getOriginAddressesFromBatchId(
        addressesUrl,
        batchId,
        createTraceHeaders(originBusinessTraceId)
      )
    );

    if (response.error) {
      setEndIcon('error');
      return [];
    }

    return response.data.map(fromAddressToConnectionAddress);
  };

  const onConfirmNmiOrMirn = async (
    address: IOriginAddressMeter | ConnectionAddressObject | undefined,
    updateAddressInputValue = true
  ) => {
    setEndIcon('validating');
    setManualAddressEntryDialogOpen(false);
    setDedicatedNmiSearchModalOpen(false);

    handleOriginAddresses([]);

    if (!address) {
      return setEndIcon('error');
    }

    const addressDetails = await getAddress(address.originAddressId ?? '');

    if (!addressDetails) {
      return setEndIcon('error');
    }

    if (updateAddressInputValue) {
      setAddressValue(addressDetails.addressLabel);
    }

    handleSelectedAddress(addressDetails);
    setEndIcon('success');
  };

  const onSelect = async (result: AddressResult) => {
    setEndIcon('validating');
    setAddressValue(result.label);

    const address = await asyncResult(fetchAddressDetails(result.addressId));

    if (address.error) {
      return setEndIcon('error');
    }

    await handleOriginAddressesLookup(address.data);
  };

  const handleOriginAddressesLookup = async (
    address: AddressManualEntryValues | Address,
    isManualSearch = false
  ) => {
    const addresses = await getAddresses(address, isManualSearch);
    handleOriginAddresses(addresses);

    setSelectedAddress(undefined);
    setUnconfirmedAddress(undefined);

    const addressDetails = await getAddress(addresses?.[0]?.originAddressId ?? '');

    if (!addressDetails) {
      return setEndIcon('error');
    }

    const connectionAddress = addElectricityDetailsToAddress(addressDetails, addresses);

    if (!isNMIHardStop(addresses) && !isNMIBelowThreshold(addresses, selectedEnergy)) {
      handleSelectedAddress(connectionAddress);
    } else {
      handleUnconfirmedAddress(connectionAddress);
    }

    setEndIcon('success');
  };

  const addElectricityDetailsToAddress = (
    address: ConnectionAddressObject,
    addresses: ConnectionAddressObject[]
  ): ConnectionAddressObject => {
    return {
      ...address,
      elecDetails: addresses?.[0]?.elecDetails || address.elecDetails,
    };
  };

  const onQueryChange = async (address: string) => {
    setShowEmptySearchError(false);
    setEndIcon('');
    setSelectedAddress(undefined);
    setUnconfirmedAddress(undefined);
    setAddressValue(address);
  };

  const onManualSearchSubmit = async (
    address: AddressManualEntryValues,
    _errors: AddressManualEntryError[]
  ) => {
    setAddressValue(constructDisplayLabel(address as Address));
    setEndIcon('validating');
    await handleOriginAddressesLookup(address, true);
  };

  const reverseAddressLookup = async (originAddressId: string) => {
    setEndIcon('validating');
    const address = await getAddress(originAddressId);

    if (!address) {
      return setEndIcon('error');
    }

    if (address.isSelected || !address.batchId) {
      setAddressValue(address.qasAddress.label);
      handleSelectedAddress(address);
    } else {
      const addresses = await getAddressesFromBatchId(address.batchId);

      const firstAddress = addresses[0];
      setAddressValue(firstAddress.qasAddress.label);
      handleOriginAddresses(addresses);
      handleSelectedAddress(address);
    }

    setEndIcon('success');
  };

  const handleSelectedAddress = (address: ConnectionAddressObject) => {
    setSelectedAddress(address);
    onAddressSelected(address);
  };

  const handleUnconfirmedAddress = (address: ConnectionAddressObject) => {
    setUnconfirmedAddress(address);
    onAddressUnconfirmed?.(address);
  };

  const handleOriginAddresses = (addresses: ConnectionAddressObject[]) => {
    setOriginAddresses(addresses);
    onAddressesUpdated?.(addresses);
  };

  const handleAddressCleared = () => {
    setShowEmptySearchError(false);
    setAddressValue('');
    setEndIcon('');
    setSelectedAddress(undefined);
    setUnconfirmedAddress(undefined);
    handleOriginAddresses([]);
    setDedicatedNmiSearchModalOpen(false);
    setNmiConfirmationDialogOpen(false);
    onAddressCleared?.();
  };

  const hasResolvedAddress = Boolean(selectedAddress || unconfirmedAddress);
  // A successful address lookup can still need CES checks or meter confirmation.
  const ctaBlocked =
    consumerCtaBlocked ||
    endIcon === 'validating' ||
    (hasResolvedAddress &&
      (nmiResolutionState === undefined ||
        nmiResolutionState === NMIResolutionState.InProgress ||
        nmiResolutionState === NMIResolutionState.NeedsToConfirmNmi));
  const showAddressLookupEmpty =
    (!addressValue || addressValue.length === 0) && (showEmptyAddressError || showEmptySearchError);

  const handleCtaClick = () => {
    if (ctaBlocked || ctaLoading) return;

    if (!hasResolvedAddress) {
      setShowEmptySearchError(true);
      return;
    }

    onCtaClick?.();
  };

  const getHelperText = (endIcon: AddressLookupProps['endIcon']): string | undefined => {
    if (showAddressLookupEmpty) {
      return 'Please enter your connection address...';
    }

    if (endIcon === 'error') {
      return 'Sorry - something went wrong. Please try again, or call 13 66 83.';
    }

    return undefined;
  };

  return (
    <>
      <UpliftedAddressLookup
        id="address-lookup"
        data-id="address-lookup"
        ariaLabel={ariaLabel}
        endIcon={endIcon}
        error={endIcon === 'error' || showAddressLookupEmpty}
        fetchAddresses={fetchAddresses}
        forcePopupIcon={false}
        hasResolvedAddress={hasResolvedAddress}
        helperText={getHelperText(endIcon)}
        manualEntry
        manualEntryProps={{
          id: 'address-lookup-manual-entry',
          'data-id': 'address-lookup-manual-entry',
          onManualEntryOpen: () => {
            setManualAddressEntryDialogOpen(true, 0);
          },
          onManualEntryClose: () => {
            setManualAddressEntryDialogOpen(false);
          },
          onManualNMIOpen: searchByNmi
            ? () => {
                setManualAddressEntryDialogOpen(true, 1);
              }
            : undefined,
          onManualMIRNOpen: searchByMirn
            ? () => {
                setManualAddressEntryDialogOpen(true, 2);
              }
            : undefined,
          onSubmit: onManualSearchSubmit,
        }}
        onClear={handleAddressCleared}
        ctaLabel={ctaLabel}
        ctaBlocked={ctaBlocked}
        ctaLoading={ctaLoading}
        onCtaClick={handleCtaClick}
        onQueryChange={onQueryChange}
        onSelect={onSelect}
        placeholder={placeholder}
        value={addressValue}
        AddressManualEntryComponent={(props) => (
          <AddressManualEntryDialog
            {...props}
            isOpen={manualAddressEntryDialogOpen}
            onClose={() => setManualAddressEntryDialogOpen(false)}
            defaultTab={manualAddressTab}
            searchByNmi={{
              enabled: searchByNmi,
              endpoint: reverseLookupUrl,
              onConfirm: onConfirmNmiOrMirn,
            }}
            searchByMirn={{
              enabled: searchByMirn,
              endpoint: reverseLookupUrl,
              onConfirm: onConfirmNmiOrMirn,
            }}
          />
        )}
      />

      {nmiResolutionState === NMIResolutionState.NmiHardStop && isNMIServiceable(addresses) && (
        <DedicatedNmiSearchModal
          autoOpen={() => {
            setDedicatedNmiSearchModalOpen(addressValueChanged && selectedEnergy.electricity);
          }}
          isOpen={dedicatedNmiSearchModalOpen}
          onClose={() => setDedicatedNmiSearchModalOpen(false)}
          searchByNmi={{
            endpoint: reverseLookupUrl,
            onConfirm: onConfirmNmiOrMirn,
          }}
        />
      )}

      <NMIResolution.ConfirmAddressDialog
        onConfirm={(address) => onConfirmNmiOrMirn(address, false)}
        onEnterAddressManually={() => {
          setManualAddressEntryDialogOpen(true, 0);
        }}
        onEnterNmiManually={() => {
          setManualAddressEntryDialogOpen(true, 1);
        }}
        open={nmiConfirmationDialogOpen}
        onClose={() => setNmiConfirmationDialogOpen(false)}
      />
      <NMIResolution.ConfirmAddressSuccess />
    </>
  );
};
